import { DragDropModule, CdkDragDrop } from '@angular/cdk/drag-drop'
import { Component, OnInit, inject, input, output, viewChild } from '@angular/core'
import { TaskListItemComponent } from '@ziptility/components/task-list-item/task-list-item.component'
import { SwimLaneHeaderComponent } from './swimlane-header'
import { BoardsStore, type SwimLane, type BoardTask } from '../../boards/boards.store'
import { User, Task } from '@ziptility/graphql'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu'
import { AuthorizationService } from '@ziptility/services'

@Component({
  selector: 'board-swimlane',
  template: `
    <mat-menu #taskMenu>
      <ng-template let-task="task" matMenuContent>
        <button mat-menu-item [mat-menu-trigger-for]="boardsMenu" [matMenuTriggerData]="{ task: task }">
          Change board
        </button>
        <button mat-menu-item (click)="onMoveToTop(task)">Move to top</button>
      </ng-template>
    </mat-menu>

    <mat-menu #boardsMenu>
      <ng-template let-task="task" matMenuContent>
        <div class="max-h-[300px]">
          @for (sl of store.swimlanes(); track sl.id) {
            <button mat-menu-item (click)="onChangeBoard(task, sl.id)">
              {{ sl.board.name }}
            </button>
          }
        </div>
      </ng-template>
    </mat-menu>


    @let swimlane = this.swimlane();

    <div
      style="visibility: hidden; position: fixed"
      [style.left]="contextMenuPosition.x"
      [style.top]="contextMenuPosition.y"
      [matMenuTriggerFor]="taskMenu"
    ></div>

    <swimlane-header
      [followerOptions]="followerOptions()"
      [swimLane]="swimlane"
      [collapsible]="collapsible()"
      [collapsed]="collapsed"
      (onDelete)="onDeleteBoard.emit()"
      (onRename)="onRenameBoard.emit()"
      (onCollapse)="collapsed = !collapsed"
    ></swimlane-header>
    <div
      class="drop-list flex-grow p-1 flew-grow overflow-auto mb-3 bg-slate-200"
      cdkDropList
      [id]="swimlane.id"
      [cdkDropListData]="swimlane.tasks"
      (cdkDropListDropped)="onCardDropped($event)"
    >
      @if (!collapsed && swimlane.tasks.length) {
        @for (task of swimlane.tasks; track task.id; let index = $index) {
          <task-list-item
            (contextmenu)="onTaskContextMenu($event, task)"
            class="cursor-pointer card block shadow-sm mb-1"
            (click)="onSelectTask.emit({ task })"
            [task]="task"
            [order]="index + 1"
            [showSelection]="showSelection()"
            [selected]="store.selectedTaskIds().includes(task.id)"
            [flexDirection]="'col'"
            cdkDrag
            [cdkDragData]="task"
            (onSelection)="store.toggleSelections([task.id])"
          ></task-list-item>
        }
      }

      @if (swimlane.pageInfo?.hasNextPage) {
        <button
          class="w-full text-center text-sm py-2 text-blue-brand underline"
          [disabled]="swimlane.loading"
          (click)="
            store.loadBoardTasks({
              boardId: swimlane.id,
              endCursor: swimlane.pageInfo?.endCursor
            })
          "
        >
          {{ swimlane.loading ? 'Loading...' : 'Load more' }}
        </button>
      }
    </div>
  `,
  host: {
    class: 'w-[350px] flex-shrink-0 flex flex-col ',
  },
  styles: [
    `
      .cdk-drag-preview {
        box-sizing: border-box;
        border-radius: 4px;
        box-shadow:
          0 5px 5px -3px rgba(0, 0, 0, 0.2),
          0 8px 10px 1px rgba(0, 0, 0, 0.14),
          0 3px 14px 2px rgba(0, 0, 0, 0.12);
      }

      .cdk-drag-placeholder {
        opacity: 0;
      }

      .cdk-drag-animating {
        transition: transform 50ms cubic-bezier(0, 0, 0.2, 1);
      }

      .drop-list.cdk-drop-list-dragging .card:not(.cdk-drag-placeholder) {
        transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
      }

      .enter-animation {
        animation: slide-fade 0.1s;
      }
      .leave-animation {
        animation: slide-fade-leave 0.1s;
      }

      @keyframes slide-fade {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes slide-fade-leave {
        from {
          opacity: 1;
          transform: translateY(0);
        }
        to {
          opacity: 0;
          transform: translateY(20px);
        }
      }
    `,
  ],
  imports: [SwimLaneHeaderComponent, TaskListItemComponent, DragDropModule, MatProgressSpinnerModule, MatMenuModule],
})
export class BoardSwimlaneComponent implements OnInit {
  readonly store = inject(BoardsStore)
  readonly authService = inject(AuthorizationService)

  readonly swimlane = input.required<SwimLane>()
  readonly taskContextMenuTrigger = viewChild.required<MatMenuTrigger>(MatMenuTrigger)
  readonly followerOptions = input<User[]>()
  readonly collapsible = input(false)
  readonly showSelection = input(false)
  readonly onDeleteBoard = output()
  readonly onRenameBoard = output()
  readonly onSelectTask = output<{ task: BoardTask }>()

  collapsed = false
  contextMenuPosition: { x: string; y: string } = { x: '0px', y: '0px' }

  ngOnInit() {
    this.store.loadBoardTasks({ boardId: this.swimlane().board.id })
  }

  onCardDropped(event: CdkDragDrop<Task[]>) {
    const { previousContainer, container, previousIndex, currentIndex: index } = event
    const swimLaneId = container.id
    const task: Task = previousContainer.data[previousIndex]
    this.store.moveTask({
      previousSwimLaneId: previousContainer.id,
      swimLaneId,
      task,
      previousIndex,
      index,
    })
  }

  onTaskContextMenu(event: MouseEvent, task: Task): void {
    if (!this.authService.canManageAllTasks) {
      return
    }

    event.preventDefault()
    const { clientX, clientY } = event
    this.contextMenuPosition.x = `${clientX}px`
    this.contextMenuPosition.y = `${clientY}px`
    this.taskContextMenuTrigger().menuData = { task }
    this.taskContextMenuTrigger().openMenu()
  }

  onChangeBoard(task: Task, boardEntityId: string): void {
    this.store.moveTask({
      previousSwimLaneId: this.swimlane()?.id,
      swimLaneId: boardEntityId,
      task,
    })
  }

  onMoveToTop(task: Task): void {
    const swimlane = this.swimlane()
    const previousIndex = swimlane?.tasks.indexOf(task)
    const { id } = swimlane

    this.store.moveTask({
      previousSwimLaneId: id,
      swimLaneId: id,
      task,
      previousIndex,
      index: 0,
    })
  }
}
